import React, { useEffect, useState } from 'react';
import { array, bool, string } from 'prop-types';
import { URL_CORE_PRICING_FOOTNOTES } from '../../end-points.js';
import useSearch from '../../hooks/use-search.js';
import AgGridReactTable from '../../ui-components/ag-gird-table/ag-grid-react-table.jsx';
import Loader from '../../ui-components/loader/loader.jsx';
import RecordCount from '../../ui-components/record-count/record-count.jsx';
import {
   API_RESPONSE_KEY_NAMES,
   API_STATUS,
   SIZE,
} from '../../utils/constants.js';
import { NOTIFICATION_MESSAGES } from '../../utils/notification-messages.js';
import { toastSuccess } from '../../utils/toasts.js';
import {
   getRecord3DetailsTableHeaders,
   getRecord3TableData,
} from './__helpers__/functions.js';
import { SEGMENTS_TABLE_HEADERS } from './__helpers__/tableHeaders.js';

const defaultTableData = {
   mainTable: [],
   segmentsTable: [],
   record3Table: [],
};

function FootnotesAndRulesTable({
   searchQuery,
   abortSearch,
   tableHeaders,
   noResultMessage,
}) {
   const [tableData, setTableData] = useState(defaultTableData);
   const { isLoading, responseData, totalRecordCount, searchStatus } =
      useSearch({
         searchQuery,
         abortSearch,
         responseKeyName: API_RESPONSE_KEY_NAMES.FOOTNOTES,
         url: URL_CORE_PRICING_FOOTNOTES,
      });
   const [selectedRows, setSelectedRows] = useState(['1']);

   const record3TableHeaders = getRecord3DetailsTableHeaders(
      tableData.record3Table[0]
   );

   const updateSelectedRows = (selectedNodes) => {
      const selectedRowIndices = selectedNodes?.map(
         (node) => node.rowIndex + 1
      );
      setSelectedRows(selectedRowIndices);
   };

   const handleMainTableRowClick = (selectedRows) => {
      if (selectedRows?.length > 1) {
         setTableData((previousData) => ({
            ...previousData,
            segmentsTable: [],
            record3Table: [],
         }));
      } else {
         const updatedSegments = selectedRows?.[0]?.data.segments || [];

         const updatedRecord3s = getRecord3TableData(
            selectedRows?.[0]?.data?.segments[0]?.record3Details
         );

         setTableData((previousData) => ({
            ...previousData,
            segmentsTable: updatedSegments,
            record3Table: updatedRecord3s,
         }));
      }

      updateSelectedRows(selectedRows);
   };

   const handleSegmentsTableRowClick = (selectedRows) => {
      if (!selectedRows?.length) return;

      if (selectedRows.length > 1) {
         setTableData((previousData) => ({
            ...previousData,
            record3Table: [],
         }));
         return;
      }

      const updatedRecord3s = getRecord3TableData(
         selectedRows[0].data.record3Details
      );
      setTableData((previousData) => ({
         ...previousData,
         record3Table: updatedRecord3s,
      }));
   };

   useEffect(() => {
      if (isLoading) {
         setTableData(defaultTableData);
         setSelectedRows(['1']);
      }

      if (!responseData?.length > 0) return;

      setTableData((tableData) => ({
         mainTable: responseData,
         segmentsTable:
            tableData?.segmentsTable?.length === 0
               ? responseData[0]?.segments
               : tableData?.segmentsTable,
         record3Table:
            tableData?.record3Table?.length === 0
               ? getRecord3TableData(
                    responseData[0]?.segments?.[0]?.record3Details
                 )
               : tableData?.record3Table,
      }));
   }, [responseData, isLoading]);

   useEffect(() => {
      if (searchStatus === API_STATUS.COMPLETED) {
         toastSuccess(NOTIFICATION_MESSAGES.SEARCH_COMPLETED);
      }
   }, [searchStatus]);

   return (
      <section className='footnotes-and-rules-table'>
         {isLoading ? (
            <div className='loader'>
               <Loader size={SIZE.SMALL} />
            </div>
         ) : tableData.mainTable.length > 0 ? (
            <>
               <div className='h-half'>
                  <AgGridReactTable
                     rowData={tableData.mainTable}
                     headers={tableHeaders}
                     onSelectedRowsChange={handleMainTableRowClick}
                     enableMultiRowSelection
                     enableRowIds
                     rememberSelectedRow
                     enableContextMenu
                     appendIncomingRowData
                     contextMenuConfig={{
                        enableCopy: true,
                        enableCopyWithHeaders: true
                     }}
                  />
               </div>

               <div className='h-half spark-flex'>
                  <div className='h-full spark-mar-r-.5 segment-table'>
                     <AgGridReactTable
                        rowData={tableData.segmentsTable}
                        headers={SEGMENTS_TABLE_HEADERS}
                        onSelectedRowsChange={handleSegmentsTableRowClick}
                        enableMultiRowSelection
                        autoSizeColumns={false}
                        selectFirstRowByDefault
                        contextMenuConfig={{
                           enableCopy: true,
                           enableCopyWithHeaders: true
                        }}
                     />
                  </div>
                  <div className='h-full flex-grow-1'>
                     <AgGridReactTable
                        rowData={tableData.record3Table}
                        headers={record3TableHeaders}
                        selectFirstRowByDefault={false}
                        contextMenuConfig={{
                           enableCopy: true,
                           enableCopyWithHeaders: true
                        }}
                     />
                  </div>
               </div>

               <RecordCount
                  selectedRowIndices={selectedRows}
                  totalRowCount={totalRecordCount}
                  searchStatus={searchStatus}
               />
            </>
         ) : (
            <p className='spark-bold grid place-items-center h-full text-lg'>
               {noResultMessage}
            </p>
         )}
      </section>
   );
}

FootnotesAndRulesTable.propTypes = {
   searchQuery: array,
   abortSearch: bool,
   tableHeaders: array,
   noResultMessage: string,
};

export default FootnotesAndRulesTable;
